// conversation-voice-input: הקלטה קולית בשורת הכתיבה. לחיצה = הקלטה (MediaRecorder) עם שעון; לחיצה שנייה (או 120 שניות) =
// עצירה ותמלול בשרת (POST /api/voice/transcribe). הטקסט עובר ל-onText: נכנס לשדה לעריכה, לא נשלח לבד. ההקלטה לא נשמרת.
// בלי תמלול במופע (GET /api/voice → enabled false) או בלי MediaRecorder בדפדפן — הכפתור לא מוצג.
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { aiApi } from '../../lib/aiApi.ts';
import { ApiError } from '../../lib/api.ts';
import { T } from '../../lib/strings.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';

type State = 'idle' | 'recording' | 'transcribing';

/** סוג ההקלטה: opus ב-webm (Chrome/Firefox/Android), mp4 (Safari/iOS); אחרת ברירת המחדל של הדפדפן. */
function pickMime(): string | undefined {
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']) if (MediaRecorder.isTypeSupported?.(m)) return m;
  return undefined;
}

const mmss = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

type Props = { disabled?: boolean; onText: (text: string) => void; onError: (textHe: string | null) => void };

export function VoiceButton(p: Props) {
  // בלי MediaRecorder/מיקרופון (דפדפן ישן, SSR) — אין כפתור ואין בקשה
  const supported = typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  return supported ? <Recorder {...p} /> : null;
}

function Recorder({ disabled, onText, onError }: Props) {
  const info = useQuery({ queryKey: ['voice'], queryFn: aiApi.voiceInfo, staleTime: 10 * 60_000, retry: false });
  const [state, setState] = useState<State>('idle');
  const [elapsed, setElapsed] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const timers = useRef<{ tick?: number; limit?: number }>({});
  const alive = useRef(true);

  const clearTimers = () => {
    window.clearInterval(timers.current.tick);
    window.clearTimeout(timers.current.limit);
    timers.current = {};
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimers();
      const r = rec.current;
      if (r && r.state !== 'inactive') {
        r.onstop = null;
        r.stop();
        r.stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  if (!info.data?.enabled) return null;
  const { maxSeconds, maxBytes } = info.data;

  const start = async () => {
    onError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      onError(T.ai.voice.denied);
      return;
    }
    let r: MediaRecorder;
    try {
      const mime = pickMime();
      r = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      onError(T.ai.voice.unsupported);
      return;
    }
    const chunks: Blob[] = [];
    const t0 = Date.now();
    r.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    r.onstop = async () => {
      clearTimers();
      stream.getTracks().forEach((t) => t.stop());
      rec.current = null;
      const blob = new Blob(chunks, { type: (r.mimeType || 'audio/webm').split(';')[0] });
      if (!alive.current) return;
      if (blob.size === 0) {
        setState('idle');
        onError(T.ai.voice.empty);
        return;
      }
      if (blob.size > maxBytes) {
        setState('idle');
        onError(T.ai.voice.failed);
        return;
      }
      setState('transcribing');
      try {
        const text = (await aiApi.transcribe(blob)).trim();
        if (!alive.current) return;
        if (text) onText(text);
        else onError(T.ai.voice.empty);
      } catch (e) {
        if (alive.current) onError(e instanceof ApiError && e.textHe ? e.textHe : T.ai.voice.failed);
      } finally {
        if (alive.current) setState('idle');
      }
    };
    rec.current = r;
    r.start(1000);
    setElapsed(0);
    setState('recording');
    timers.current.tick = window.setInterval(() => setElapsed(Date.now() - t0), 250);
    timers.current.limit = window.setTimeout(() => stop(), maxSeconds * 1000);
  };
  const stop = () => {
    const r = rec.current;
    if (r && r.state !== 'inactive') r.stop();
  };

  const recording = state === 'recording';
  const busy = state === 'transcribing';
  return (
    <>
      {recording && (
        <span className="flex shrink-0 items-center gap-1.5 self-center text-sm tabular-nums text-error" role="status" aria-live="polite" data-testid="voice-timer">
          <span aria-hidden="true" className="size-2 animate-pulse rounded-full bg-error" />
          <span className="sr-only">{T.ai.voice.recording(mmss(elapsed))}</span>
          <span aria-hidden="true">{mmss(elapsed)}</span>
        </span>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('shrink-0', recording && 'bg-error/10 text-error hover:bg-error/15 hover:text-error')}
        onClick={() => (recording ? stop() : void start())}
        disabled={(disabled && !recording) || busy}
        aria-label={busy ? T.ai.voice.transcribing : recording ? T.ai.voice.stop : T.ai.voice.start}
        aria-pressed={recording}
        title={recording ? T.ai.voice.stop : T.ai.voice.hint}
        data-testid="voice-btn"
        data-state={state}
      >
        {busy ? (
          <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5 animate-spin" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M21 12a9 9 0 1 1-6.2-8.6" />
          </svg>
        ) : recording ? (
          <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="currentColor">
            <rect x="6.5" y="6.5" width="11" height="11" rx="2" />
          </svg>
        ) : (
          <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
          </svg>
        )}
      </Button>
    </>
  );
}
